import { DateTimeUtils } from "@stellar/utils";
import { DateTime } from "luxon";

/** The unit the length of a plan is expressed in */
export type PlanDurationType = "days" | "years" | "unlimited";

/** How each duration type is presented to the user */
export const PLAN_DURATION_LABELS: Record<
  PlanDurationType,
  {
    /** The entry of the duration type in the dropdown */
    option: string;

    /** The label of the field holding the amount of units */
    amountField: string;
  }
> = {
  days: { option: "Days", amountField: "Days" },
  years: { option: "Years", amountField: "Years" },
  unlimited: { option: "Unlimited", amountField: "Duration" },
};

/** All duration types, in the order they are offered to the user */
export const PLAN_DURATION_TYPES = [
  "days",
  "years",
  "unlimited",
] as const satisfies readonly PlanDurationType[];

/** The end date given to plans that should never expire */
export const UNLIMITED_PLAN_END_DATE = "2099-01-01T00:00:00.000Z";

/** The longest duration a subscription is normally sold for */
export const MAX_USUAL_SUBSCRIPTION_YEARS = 3;

/** The length of a plan, as entered by the user */
export interface PlanDuration {
  /** The unit the duration is expressed in */
  durationType: PlanDurationType;

  /** How many units of `durationType` the plan lasts. Unused for an unlimited duration */
  durationValue: number;
}

/** The duration a plan gets before the user picks a product */
export const DEFAULT_PLAN_DURATION: PlanDuration = {
  durationType: "years",
  durationValue: 1,
};

/** Why the configured duration cannot be used, or null when it is valid */
export function validatePlanDuration({
  durationType,
  durationValue,
}: PlanDuration): string | null {
  if (durationType === "unlimited" || durationValue > 0) {
    return null;
  }

  return `Please enter a valid number of ${PLAN_DURATION_LABELS[
    durationType
  ].amountField.toLowerCase()}`;
}

interface CalculatePlanEndDateProps extends PlanDuration {
  /** The start date of the plan, in milliseconds */
  startDate: number;
}

/** Returns the end date of a plan, which is always derived from its start date and its duration */
export function calculatePlanEndDate({
  startDate,
  durationType,
  durationValue,
}: CalculatePlanEndDateProps): number {
  switch (durationType) {
    case "unlimited":
      return Date.parse(UNLIMITED_PLAN_END_DATE);

    case "years":
      // A plan ends the day before its anniversary, so it covers whole years without overlapping.
      // Luxon keeps the calendar day across leap years, clamping Feb 29th to Feb 28th
      return DateTime.fromMillis(startDate)
        .plus({ years: durationValue })
        .minus({ days: 1 })
        .toMillis();

    case "days":
      // A plan lasting a single day starts and ends on the same day
      return DateTimeUtils.addDaysToDate(
        startDate,
        durationValue - 1
      ).getTime();
  }
}

/** How far a plan that should end "now" is moved into the future, in milliseconds (1 second) */
export const ENDING_NOW_OFFSET_MS = 1_000;

/**
 * Returns the moment a plan that should end "now" ends, and the plan that replaces it starts.
 *
 * "Now" is taken in the browser, so by the time the request reaches the backend it already lies
 * some milliseconds or even seconds in the past, and the backend would receive an end date that
 * has passed. It is moved some seconds ahead to keep it in the future. The replacing plan starts at the
 * very same moment, so that the two plans neither overlap nor leave a gap between them.
 */
export function getEndingNowDate(): number {
  return Date.now() + ENDING_NOW_OFFSET_MS;
}

/**
 * Returns the end date of a plan that continues another one, which is exactly its duration after
 * its start date. Such a plan starts at the very moment the other plan ends instead of at the start
 * of a day, so rounding it to whole days would make it end a day too early.
 */
export function calculateContinuingPlanEndDate({
  startDate,
  durationType,
  durationValue,
}: CalculatePlanEndDateProps): number {
  if (durationType === "unlimited") {
    return Date.parse(UNLIMITED_PLAN_END_DATE);
  }

  return DateTime.fromMillis(startDate)
    .plus({ [durationType]: durationValue })
    .toMillis();
}

/** Expresses a duration configured in days as whole years whenever it is an exact multiple of a year */
export function planDurationFromDays(days: number): PlanDuration {
  const { inYear } = DateTimeUtils.DURATION.days;

  return days > 0 && days % inYear === 0
    ? { durationType: "years", durationValue: days / inYear }
    : { durationType: "days", durationValue: days };
}

interface PlanDateRange {
  /** The start date of the plan, in milliseconds */
  startDate: number;

  /** The end date of the plan, in milliseconds */
  endDate: number;
}

/** The number of days a plan covers, counting both the day it starts and the day it ends */
export function planDurationInDays({
  startDate,
  endDate,
}: PlanDateRange): number {
  return DateTimeUtils.diffBetweenTwoDatesInDays(endDate, startDate) + 1;
}

/**
 * The duration of an existing plan, in whole years whenever it lasts that long, and in days otherwise.
 * A plan that is up to a day shorter or longer than whole years still counts as lasting them, e.g.
 * one of 364 to 366 days lasts a year, as plans are often created a day off their anniversary.
 * It is measured by the time that passes between the dates instead of by calendar days, so that it
 * does not depend on the time zone the plan was created in.
 */
export function planDurationBetween({
  startDate,
  endDate,
}: PlanDateRange): PlanDuration {
  // The end date is the last moment of the plan, so the plan lasts until right after it
  const end = DateTime.fromMillis(endDate).plus({ milliseconds: 1 });
  const start = DateTime.fromMillis(startDate);

  const years = Math.round(end.diff(start, "years").years);
  if (
    years > 0 &&
    Math.abs(start.plus({ years }).diff(end, "days").days) <= 1
  ) {
    return { durationType: "years", durationValue: years };
  }

  return {
    durationType: "days",
    durationValue: Math.max(Math.round(end.diff(start, "days").days), 1),
  };
}

/** Whether a plan lasts longer than the longest duration a subscription is normally sold for */
export function isUnusuallyLongSubscription({
  startDate,
  endDate,
}: PlanDateRange): boolean {
  // End dates are inclusive, so a plan of exactly the maximum ends the day before the anniversary
  return (
    endDate >=
    DateTime.fromMillis(startDate)
      .plus({ years: MAX_USUAL_SUBSCRIPTION_YEARS })
      .toMillis()
  );
}
